
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Implementing the OAuth flow',
  layout: 'guide',
  description: 'Step-by-step guide to the Authorization Code Grant with OIDC',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Implementing the OAuth flow' subtitle={metadata.description} mdxType="Header" />
      <p><strong parentName="p">{`Prerequisites:`}</strong>{` Before implementing the OAuth flow, ensure you have your `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup"
        }}>{`OAuth server configured`}</a>{`, client credentials generated, JWKS endpoint accessible, and consistency key implementation chosen.`}</p>
      <p>{`For a runnable walkthrough of the full flow, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
        }}>{`FDX Explorer`}</a>{` on the Authentication overview.`}</p>
      <h2 {...{
        "id": "flow-at-a-glance"
      }}>{`Flow at a glance`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Flow at a glance",
          "#": true,
          "Flow": true,
          "at": true,
          "a": true,
          "glance": true
        }}>{`User -> Plaid -> Your /authorize -> User authenticates -> Your /token -> Plaid validates -> API access
       (redirect)                  (login + 2FA)         (code exchange)  (JWKS check)
`}</code></pre>
      <h2 {...{
        "id": "the-oauth-flow-step-by-step"
      }}>{`The OAuth flow, step by step`}</h2>
      <p>{`The Authorization Code Grant follows a five-step process. This guide describes the `}<a parentName="p" {...{
          "href": "https://openid.net/specs/"
        }}>{`OIDC flow`}</a>{`. Plain OAuth 2.0 follows the same flow but without the ID token validation step.`}</p>
      <h3 {...{
        "id": "screen-placement"
      }}>{`Screen placement`}</h3>
      <p>{`Under regulatory requirements, the data recipient (or its platform) is responsible for obtaining user authorization. Plaid fulfills this obligation on behalf of apps via Plaid Link screens, divided as follows:`}</p>
      <p><strong parentName="p">{`Data Provider hosts (your responsibility):`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Authentication screen`}</strong>{` - Users log in with their credentials on your domain.`}</li>
        <li parentName="ul"><strong parentName="li">{`Account selection (optional)`}</strong>{` - If you are not using Plaid account selection, you may host account selection on your domain. This approach works but adds friction and limits the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/planning#account-selection-where-to-place-the-screen"
          }}>{`user experience for returning users`}</a>{`, so Plaid does not recommend it for most integrations.`}</li>
      </ul>
      <p><strong parentName="p">{`Plaid hosts (handled by Plaid Link, recommended):`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Data transparency messaging`}</strong>{` - Clear identification of which app is requesting access and what data will be shared.`}</li>
        <li parentName="ul"><strong parentName="li">{`Account selection`}</strong>{` - User selects which accounts to share in Plaid Link after authenticating with you. Plaid recommends this model as it provides the best user experience for returning users.`}</li>
      </ul>
      <Callout warning mdxType="Callout">
        <p>{`Keep the flow streamlined. Beyond the authentication screen (and, if you choose DP-owned account selection, that screen), avoid inserting additional pages into the end-to-end linking flow. Every extra screen increases drop-off.`}</p>
      </Callout>
      <p><strong parentName="p">{`The five steps:`}</strong></p>
      <ol>
        <li parentName="ol"><a parentName="li" {...{
            "href": "#step-1---plaid-redirects-the-end-user"
          }}>{`Plaid redirects your user to authenticate`}</a></li>
        <li parentName="ol"><a parentName="li" {...{
            "href": "#step-2---the-user-completes-all-authentication-steps"
          }}>{`User logs in and authorizes access`}</a></li>
        <li parentName="ol"><a parentName="li" {...{
            "href": "#step-3---you-create-an-authorization-code-and-send-it-to-plaid"
          }}>{`You send Plaid an authorization code`}</a></li>
        <li parentName="ol"><a parentName="li" {...{
            "href": "#step-4---plaid-requests-an-access-token"
          }}>{`Plaid exchanges the code for tokens`}</a></li>
        <li parentName="ol"><a parentName="li" {...{
            "href": "#step-5---plaid-verifies-the-id-token"
          }}>{`Plaid verifies the ID token and accesses your API`}</a></li>
      </ol>
      <h2 {...{
        "id": "step-1---plaid-redirects-the-end-user"
      }}>{`Step 1 - Plaid redirects the end user`}</h2>
      <p>{`When a user starts linking their account, Plaid redirects them to your authorization endpoint (as defined in your `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
        }}>{`well-known config`}</a>{`). The redirect includes these query parameters:`}</p>
      <h3 {...{
        "id": "query-parameters"
      }}>{`Query parameters`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Query parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Value`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`response_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A string, indicating the type of response Plaid expects.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Set to: `}<inlineCode parentName="td">{`code`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`redirect_uri`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where Plaid expects your organization to redirect back to once the user completes all authentication steps.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A set of strings indicating the set of scopes Plaid requests access to.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Will be set to `}<inlineCode parentName="td">{`openid`}</inlineCode>{` and `}<inlineCode parentName="td">{`offline_access`}</inlineCode>{`, at minimum`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`client_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The `}<a parentName="td" {...{
                "href": "/core-exchange/docs/authentication/oauth-server-setup#client-id"
              }}>{`client ID`}</a>{` you issued to Plaid.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A client ID`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`state`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Your organization will return the same string when redirecting to the `}<inlineCode parentName="td">{`redirect_uri`}</inlineCode>{`.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`prompt`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Specifies the type of authentication prompt the server will send to the end user.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Set to `}<inlineCode parentName="td">{`login`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code_challenge`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><em parentName="td">{`Optional:`}</em>{` This value is required when using PKCE.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The `}<inlineCode parentName="td">{`code_verifier`}</inlineCode>{`, either as a Base64URL-encoded hash or in plain text`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code_challenge_method`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><em parentName="td">{`Optional:`}</em>{` This value is required when using PKCE.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Indicates the format of the `}<inlineCode parentName="td">{`code_challenge`}</inlineCode>{`. May be either: `}<inlineCode parentName="td">{`S256`}</inlineCode>{` (hashed by SHA-256 and Base64URL-encoded) or `}<inlineCode parentName="td">{`plain`}</inlineCode>{` (in plain text)`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example redirect",
          "#": true,
          "Example": true,
          "redirect": true
        }}>{`https://auth.firstplatypus.com/oauth2/v1/authorize?response_type=code
&client_id=dc1fe34ae9e5e98147f2fd76060016a4
&redirect_uri=https%3A%2F%2Fcdn.plaid.com%2Flink%2Fv2%2Fstable%2Foauth.html
&state=v2.9f77edf0-a328-4501-9528-4a5f460cf770.0.0
&scope=openid%20offline_access%20accounts%20transactions
&prompt=login
&code_challenge=4dKRcTlKg7PbBxYokEH5gbfwfXcUdvDVYVdFZniVq4s
&code_challenge_method=S256
`}</code></pre>
      <p>{`This page will be requested directly by the user's device.`}</p>
      <h2 {...{
        "id": "step-2---the-user-completes-all-authentication-steps"
      }}>{`Step 2 - The user completes all authentication steps`}</h2>
      <p>{`At this step, the user authenticates with your system, typically username/password plus 2FA. If the user cancels at any point, `}<a parentName="p" {...{
          "href": "#user-cancellation"
        }}>{`handle it as an error`}</a>{` and send them back to Plaid with the right error code.`}</p>
      <h3 {...{
        "id": "two-factor-authentication-2fa"
      }}>{`Two-factor authentication (2FA)`}</h3>
      <p><strong parentName="p">{`Plaid requires 2FA`}</strong>{` for Core Exchange connections. Users must complete a second authentication factor beyond username and password. 2FA aligns with industry security standards and reduces the risk of account takeover.`}</p>
      <p>{`Use industry-standard 2FA methods such as one-time codes (SMS, email, authenticator apps), push notifications, biometric authentication, or hardware security keys. For mobile apps, biometric authentication provides the best user experience.`}</p>
      <p><strong parentName="p">{`2FA communication requirements:`}</strong></p>
      <p>{`When sending 2FA prompts (SMS, email, push notification), the message must:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Be clear about which app is requesting access`}</strong>{` - Include the app name from Plaid's authorization request`}</li>
        <li parentName="ul"><strong parentName="li">{`Look legitimate`}</strong>{` - Use your standard templates and sender information to avoid appearing like phishing`}</li>
        <li parentName="ul"><strong parentName="li">{`Be timely`}</strong>{` - Deliver within seconds of the authentication attempt`}</li>
      </ul>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example SMS",
          "#": true,
          "Example": true,
          "SMS": true
        }}>{`First Platypus Bank: Verify connection to Venmo.
Your code: 123456
If you didn't request this, contact us immediately.
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example email",
          "#": true,
          "Example": true,
          "email": true
        }}>{`Subject: Verify connection to Venmo

Hi [Name],

Venmo is requesting access to your First Platypus Bank account.

Your verification code: 123456

This code expires in 10 minutes.

If you didn't request this, please contact us immediately.
`}</code></pre>
      <h3 {...{
        "id": "mobile-authentication"
      }}>{`Mobile authentication`}</h3>
      <p>{`For mobile apps, enable `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/app2app"
        }}>{`App2App`}</a>{` and `}<a parentName="p" {...{
          "href": "/core-exchange/docs/user-experience#webauthn"
        }}>{`biometric authentication`}</a>{`. Your users will thank you.`}</p>
      <h2 {...{
        "id": "step-3---you-create-an-authorization-code-and-send-it-to-plaid"
      }}>{`Step 3 - You create an authorization code and send it to Plaid`}</h2>
      <p>{`After the user successfully authenticates, generate a temporary authorization code and redirect the user's browser back to Plaid's `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`. Include these query parameters:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Query parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The temporary authorization code. Plaid will exchange this for an access token in the `}<a parentName="td" {...{
                "href": "#step-4---plaid-requests-an-access-token"
              }}>{`next step`}</a>{`.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`state`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The `}<inlineCode parentName="td">{`state`}</inlineCode>{` parameter from `}<a parentName="td" {...{
                "href": "#step-1---plaid-redirects-the-end-user"
              }}>{`step 1`}</a>{`. Plaid verifies that the two values match.`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "example-of-your-response"
      }}>{`Example of your response`}</h3>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example of your response",
          "#": true,
          "Example": true,
          "of": true,
          "your": true,
          "response": true
        }}>{`https://cdn.plaid.com/link/v2/stable/oauth.html?code=1284918391
&state=eyJvYXV0aF9zdGF0ZV
`}</code></pre>
      <h2 {...{
        "id": "step-4---plaid-requests-an-access-token"
      }}>{`Step 4 - Plaid requests an access token`}</h2>
      <p>{`Now, Plaid calls your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{` to exchange the authorization code for tokens. This is a backend-to-backend call (no user involved), authenticated using the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#credentials"
        }}>{`client ID and secret`}</a>{` you issued.`}</p>
      <p>{`The auth method comes from your `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
        }}>{`well-known config`}</a>{`. If you specified `}<inlineCode parentName="p">{`client_secret_basic`}</inlineCode>{`, Plaid will send a `}<a parentName="p" {...{
          "href": "https://www.twilio.com/docs/glossary/what-is-basic-authentication"
        }}>{`basic auth`}</a>{` header.`}</p>
      <p>{`Request body format: `}<inlineCode parentName="p">{`application/x-www-form-urlencoded`}</inlineCode>{`. Plaid sends these parameters:`}</p>
      <h3 {...{
        "id": "body-parameters"
      }}>{`Body parameters`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Body parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Value`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`prompt`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Specifies the type of a prompt the server will send to the end user.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Will be set to: `}<inlineCode parentName="td">{`consent`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`grant_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The type of grant Plaid is exchanging for an access token. (In this case, an authorization code.)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Will be set to: `}<inlineCode parentName="td">{`authorization_code`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The temporary authorization code Plaid is exchanging for the access token.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The `}<inlineCode parentName="td">{`code`}</inlineCode>{` you sent to Plaid in the `}<a parentName="td" {...{
                "href": "#step-3---you-create-an-authorization-code-and-send-it-to-plaid"
              }}>{`previous step`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`redirect_uri`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where Plaid expects your organization to redirect back to once the user completes all authentication steps.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The set of possible scopes.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A space-delimited list of scopes, for example: `}<inlineCode parentName="td">{`offline_access openid accounts transactions identity`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`audience`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><em parentName="td">{`Optional:`}</em>{` Defines who the token is for (the resource server that will consume the token). Plaid will send this value if this information is available.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A string, for example: `}<inlineCode parentName="td">{`https://api.firstplatypusbank.com`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code_verifier`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><em parentName="td">{`Optional:`}</em>{` This value is only required when using PKCE.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The `}<inlineCode parentName="td">{`code_verifier`}</inlineCode>{`, created before starting the authentication flow`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Plaid request example",
          "#": true,
          "Plaid": true,
          "request": true,
          "example": true
        }}>{`curl --request POST 'https://auth.firstplatypusbank.com/oauth2/v1/token' \\
--header 'Content-Type: application/x-www-form-urlencoded' \\
--header "Authorization: Basic YzVhNTI0NWIwNjJiZjg0MjBkMTFhYjQzNjFiMjhhMTU6clZYWU9vUVM0ckhVRzc5bl80OGFs" \\
--data-urlencode 'prompt=consent' \\
--data-urlencode 'grant_type=authorization_code' \\
--data-urlencode 'code=1284918391' \\
--data-urlencode 'redirect_uri=https://cdn.plaid.com/link/v2/stable/oauth.html' \\
--data-urlencode 'scope=offline_access accounts'
`}</code></pre>
      <p>{`Validate that the `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`client_secret`}</inlineCode>{`, `}<inlineCode parentName="p">{`code`}</inlineCode>{`, and `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` all match what you expect. If everything checks out, send Plaid the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`, plus an `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` if you implement OIDC. Plain OAuth 2.0 implementations don't issue an ID token and `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#how-to-provide-it"
        }}>{`supply the user identifier through `}<inlineCode parentName="a">{`/customers/current`}</inlineCode></a>{` instead.`}</p>
      <h3 {...{
        "id": "response-parameters"
      }}>{`Response parameters`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Property`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`access_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string (likely a JWT structured according to the `}<a parentName="td" {...{
                "href": "https://datatracker.ietf.org/doc/html/rfc9068#section-2.2"
              }}>{`JWT profile for OAuth 2.0 access tokens`}</a>{`). Plaid will present this string as a bearer token to all requests made to your Core Exchange API. The `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` encodes the user's identity and the scope of access granted.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`expires_in`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The lifetime of the access token, in seconds. Typically 15 minutes (900 seconds). Plaid checks for expiration before using an access token. If the access token has expired, Plaid will use the refresh token to request a new access token. If your organization expires the token before the stated expiration date, Plaid expects to receive a 401 response with an error code of "602 not authorized".`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`id_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An `}<a parentName="td" {...{
                "href": "https://openid.net/specs/openid-connect-core-1_0.html#IDToken"
              }}>{`OIDC ID token`}</a>{`. Plaid only reads the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field from this token. In a deployment with multiple financial institutions, the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field must be unique to each financial institution. (It doesn't need to be unique to the user across all financial institutions.)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string (likely a JWT) that Plaid can use to request a new access token. Plaid will use this to fetch data periodically long after the original access token expires. See the `}<a parentName="td" {...{
                "href": "#refresh-flow"
              }}>{`refresh flow`}</a>{` section for more information. `}<strong parentName="td">{`Recommended expiration: 13+ months.`}</strong>{` Compliance requires reauthorization every 12 months, which Plaid handles automatically. Setting refresh token expiration to 13+ months ensures tokens remain valid through the reauthorization cycle. Don't use inactivity timeouts; some use cases involve infrequent API calls that shouldn't cause the connection to be invalidated.`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example of your response",
          "#": true,
          "Example": true,
          "of": true,
          "your": true,
          "response": true
        }}>{`{
  "access_token": "agstynmdygjdghabrgraeh...",
  "expires_in": 900,
  "id_token": "snsyjrhvjdtvyjvsgcegaethstj...",
  "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu..."
}
`}</code></pre>
      <h3 {...{
        "id": "error-response"
      }}>{`Error response`}</h3>
      <p>{`If the token request fails, return HTTP 400 with a JSON body containing an `}<inlineCode parentName="p">{`error`}</inlineCode>{` field, as defined in the `}<a parentName="p" {...{
          "href": "https://datatracker.ietf.org/doc/html/rfc6749#section-5.2"
        }}>{`OAuth 2.0 specification`}</a>{`. The `}<inlineCode parentName="p">{`error_description`}</inlineCode>{` and `}<inlineCode parentName="p">{`error_uri`}</inlineCode>{` fields are optional but recommended for debugging.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example error response",
          "#": true,
          "Example": true,
          "error": true,
          "response": true
        }}>{`{
  "error": "invalid_grant",
  "error_description": "Authorization code has expired",
  "error_uri": "https://auth.firstplatypusbank.com/errors/invalid-grant"
}
`}</code></pre>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Property`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Required`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`error`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An `}<a parentName="td" {...{
                "href": "#token-error-codes"
              }}>{`error code`}</a>{` from the token error codes table.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`error_description`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A human-readable description of the error.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`error_uri`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A URI identifying a human-readable web page with error details.`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`Common errors for `}<inlineCode parentName="p">{`grant_type: "authorization_code"`}</inlineCode>{`:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_grant`}</inlineCode>{`: the authorization code is expired, already used, or the `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` doesn't match`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_request`}</inlineCode>{`: a required parameter is missing or malformed`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_client`}</inlineCode>{`: client authentication failed`}</li>
      </ul>
      <p>{`See `}<a parentName="p" {...{
          "href": "#token-error-codes"
        }}>{`Token error codes`}</a>{` for the full list.`}</p>
      <h2 {...{
        "id": "step-5---plaid-verifies-the-id-token"
      }}>{`Step 5 - Plaid verifies the ID token`}</h2>
      <Callout mdxType="Callout">
  This step only applies to OIDC implementations. Plain OAuth 2.0
  implementations skip this step since there's no ID token to validate.
      </Callout>
      <p>{`After receiving the `}<inlineCode parentName="p">{`id_token`}</inlineCode>{`, Plaid verifies its authenticity by fetching your JWKS from the `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` in your `}<inlineCode parentName="p">{`.well-known`}</inlineCode>{` config.`}</p>
      <p><strong parentName="p">{`How verification works:`}</strong></p>
      <ol>
        <li parentName="ol">{`Fetch JWKS from your `}<inlineCode parentName="li">{`jwks_uri`}</inlineCode>{` (Plaid caches this for up to 10 minutes).`}</li>
        <li parentName="ol">{`Use the `}<inlineCode parentName="li">{`kid`}</inlineCode>{` in the token header to find the right public key.`}</li>
        <li parentName="ol">{`Verify the digital signature.`}</li>
        <li parentName="ol">{`Extract the `}<inlineCode parentName="li">{`sub`}</inlineCode>{` field (the user identifier).`}</li>
      </ol>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Quick recap",
          "#": true,
          "Quick": true,
          "recap": true
        }}>{`User -> /authorize -> authenticates
-> /token -> returns tokens
-> Plaid fetches JWKS -> verifies ID token -> Done
`}</code></pre>
      <p>{`Plaid now has everything needed to access your Core Exchange API on behalf of the user.`}</p>
      <h2 {...{
        "id": "refresh-flow"
      }}>{`Refresh flow`}</h2>
      <p>{`Access tokens expire. That's by design. When Plaid needs fresh data (e.g., balance checks before ACH transfers or transaction updates), it uses the refresh token to get a new access token. Same `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`, different parameters.`}</p>
      <h3 {...{
        "id": "body-parameters-1"
      }}>{`Body parameters`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Body parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Value`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`grant_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Specifies that Plaid is requesting a new access token to replace the expired access token.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Will be set to: `}<inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The refresh token you issued to Plaid. `}<strong parentName="td">{`Recommended expiration: 13+ months`}</strong>{`. This expiration aligns with the reauthorization requirement (12 months) while providing a buffer for high-traffic periods, such as tax season, when users may not complete reauthorization immediately.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Example: `}<inlineCode parentName="td">{`dhcsrtjsrgayvkdisfdgntshstu...`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Plaid request example",
          "#": true,
          "Plaid": true,
          "request": true,
          "example": true
        }}>{`curl --request POST 'https://auth.firstplatypusbank.com/oauth2/v1/token' \\
--header 'Content-Type: application/x-www-form-urlencoded' \\
--header "Authorization: Basic YzVhNTI0NWIwNjJiZjg0MjBkMTFhYjQzNjFiMjhhMTU6clZYWU9vUVM0ckhVRzc5bl80OGFs" \\
--data-urlencode 'grant_type=refresh_token' \\
--data-urlencode 'refresh_token=dhcsrtjsrgayvkdisfdgntshstu...'
`}</code></pre>
      <p>{`Validate the `}<inlineCode parentName="p">{`grant_type`}</inlineCode>{` and `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`, then respond with new tokens: `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`id_token`}</inlineCode>{`, and `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`.`}</p>
      <p><strong parentName="p">{`Pro tip:`}</strong>{` Rotate the refresh token each time for better security (new token, new expiration). But if you need to reuse the same refresh token across multiple requests, that works too.`}</p>
      <h3 {...{
        "id": "response-parameters-1"
      }}>{`Response parameters`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Property`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`access_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string (likely a JWT structured according to the `}<a parentName="td" {...{
                "href": "https://datatracker.ietf.org/doc/html/rfc9068#section-2.2"
              }}>{`JWT profile for OAuth 2.0 access tokens`}</a>{`). Plaid will present this string as a bearer token to all requests made to your Core Exchange API. The `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` encodes the user's identity and the scope of access granted.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`expires_in`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The lifetime of the access token, in seconds. Typically 15 minutes (900 seconds). Plaid checks for expiration before using an access token. If the access token has expired, Plaid will use the refresh token to request a new access token. If your organization expires the token before the stated expiration date, Plaid expects to receive a 401 response with an error code of "602 not authorized".`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`id_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An `}<a parentName="td" {...{
                "href": "https://openid.net/specs/openid-connect-core-1_0.html#IDToken"
              }}>{`OIDC ID token`}</a>{`. Plaid only reads the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field from this token. In a deployment with multiple financial institutions, the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field must be unique to each financial institution. (It doesn't need to be unique to the user across all financial institutions.)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An opaque string (likely a JWT) that can be used to request a new access token. When using refresh token rotation, a new refresh token replaces the one sent in the request. If not using rotation, this may be the same refresh token. `}<strong parentName="td">{`Recommended expiration: 13+ months.`}</strong>{` Set to at least 12 months to comply with reauthorization requirements. The 13+ month range provides buffer time for users to complete reauthorization during peak periods without connections failing.`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example success response",
          "#": true,
          "Example": true,
          "success": true,
          "response": true
        }}>{`{
  "access_token": "lngarogglkcangasgabba...",
  "expires_in": 900,
  "id_token": "snsyjrhvjdtvyjvsgcegaethstj...",
  "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu..."
}
`}</code></pre>
      <h3 {...{
        "id": "error-response-1"
      }}>{`Error response`}</h3>
      <p>{`If the refresh request fails, return HTTP 400 using the same error response format as the `}<a parentName="p" {...{
          "href": "#error-response"
        }}>{`token exchange`}</a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example error response",
          "#": true,
          "Example": true,
          "error": true,
          "response": true
        }}>{`{
  "error": "invalid_grant",
  "error_description": "Refresh token has been revoked",
  "error_uri": "https://auth.firstplatypusbank.com/errors/invalid-grant"
}
`}</code></pre>
      <p>{`Common errors for `}<inlineCode parentName="p">{`grant_type: "refresh_token"`}</inlineCode>{`:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_grant`}</inlineCode>{`: the refresh token is expired or has been revoked`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_request`}</inlineCode>{`: a required parameter is missing or malformed`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`invalid_client`}</inlineCode>{`: client authentication failed`}</li>
      </ul>
      <p>{`See `}<a parentName="p" {...{
          "href": "#token-error-codes"
        }}>{`Token error codes`}</a>{` for the full list.`}</p>
      <h2 {...{
        "id": "quick-troubleshooting"
      }}>{`Quick troubleshooting`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Symptom`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Common cause`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Fix`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`User stuck on loading screen`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Authorization endpoint timeout`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Check endpoint latency < 3.5s`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`"Invalid code" errors`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Code reuse or expiration`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Expire codes after 10 min, single-use`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Token validation fails`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`JWKS not accessible`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Verify `}<inlineCode parentName="td">{`jwks_uri`}</inlineCode>{` is public HTTPS`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Refresh token rejected`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Token expired`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Set expiration to 13+ months`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_grant`}</inlineCode>{` errors`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Code/redirect URI mismatch`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Verify exact match on redirect_uri`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`CORS errors`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Missing headers`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Add `}<inlineCode parentName="td">{`Access-Control-Allow-Origin`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
      <p>{`See the `}<a parentName="p" {...{
          "href": "#when-things-go-wrong"
        }}>{`full error reference`}</a>{` below, along with the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/troubleshooting"
        }}>{`Troubleshooting Guide`}</a>{` for detailed debugging. For the latency, uptime, and conversion targets your OAuth endpoints should meet, and how to instrument each step of the flow, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch#target-metrics"
        }}>{`target metrics`}</a>{`.`}</p>
      <h2 {...{
        "id": "when-things-go-wrong"
      }}>{`When things go wrong`}</h2>
      <p>{`OAuth errors fall into three categories, each handled differently. (Full details in `}<a parentName="p" {...{
          "href": "https://www.rfc-editor.org/rfc/rfc6749#section-4.1.2.1"
        }}>{`RFC 6749 section 4.1`}</a>{`.)`}</p>
      <Callout warning mdxType="Callout">
        <p><strong parentName="p">{`What if the `}<inlineCode parentName="strong">{`redirect_uri`}</inlineCode>{` doesn't match?`}</strong>{` If the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` in the authorization request doesn't match the registered value for that `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, don't redirect. Show an error page instead. For Plaid, the redirect URI should always be `}<inlineCode parentName="p">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode>{`. Redirecting to an unregistered URI is a security risk that could expose authorization codes to attackers.`}</p>
      </Callout>
      <h3 {...{
        "id": "user-cancellation"
      }}>{`User cancellation`}</h3>
      <p>{`If the user cancels authentication or another error occurs (excluding redirect URI mismatches), redirect them back to Plaid with these parameters:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Query parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`error`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The reason for the error. See the `}<a parentName="td" {...{
                "href": "#authorization-error-codes"
              }}>{`Authorization errors`}</a>{` table below for a list of possible errors.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`state`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The opaque string Plaid passed as the `}<inlineCode parentName="td">{`state`}</inlineCode>{` parameter in the `}<a parentName="td" {...{
                "href": "#step-1---plaid-redirects-the-end-user"
              }}><inlineCode parentName="a">{`authorization_endpoint`}</inlineCode></a>{` redirect step.`}</td>
          </tr>
        </tbody>
      </table>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example error response",
          "#": true,
          "Example": true,
          "error": true,
          "response": true
        }}>{`https://cdn.plaid.com/link/v2/stable/oauth.html?error=access_denied
&state=eyJvYXV0aF9zdGF0ZV
`}</code></pre>
      <h3 {...{
        "id": "authorization-error-codes"
      }}>{`Authorization error codes`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Error value`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_request`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`unauthorized_client`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The client is not authorized to request an authorization code using this method.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`access_denied`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The resource owner or authorization server denied the request.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`unsupported_response_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The authorization server does not support obtaining an authorization code using this method.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The requested scope is invalid, unknown, or malformed.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`server_error`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The authorization server encountered an unexpected condition that prevented it from fulfilling the request. (This error code is needed because a 500 Internal Server Error HTTP status code cannot be returned to the client via an HTTP redirect.)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`temporarily_unavailable`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The authorization server is currently unable to handle the request due to a temporary overload or maintenance of the server. (This error code is needed because a 503 Service Unavailable HTTP status code can't be returned to the client via an HTTP redirect.)`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "token-exchange-and-refresh-flow-errors"
      }}>{`Token exchange and refresh flow errors`}</h3>
      <p>{`Errors during `}<a parentName="p" {...{
          "href": "#step-4---plaid-requests-an-access-token"
        }}>{`token exchange`}</a>{` or `}<a parentName="p" {...{
          "href": "#refresh-flow"
        }}>{`refresh`}</a>{`? Return HTTP 400 with an `}<inlineCode parentName="p">{`error`}</inlineCode>{` field from the `}<a parentName="p" {...{
          "href": "#token-error-codes"
        }}>{`table below`}</a>{`. Add `}<inlineCode parentName="p">{`error_description`}</inlineCode>{` and `}<inlineCode parentName="p">{`error_uri`}</inlineCode>{` if you want to help with debugging. (Details in `}<a parentName="p" {...{
          "href": "https://datatracker.ietf.org/doc/html/rfc6749#section-5.2"
        }}>{`RFC 6749 section 5.2`}</a>{`.)`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example error response",
          "#": true,
          "Example": true,
          "error": true,
          "response": true
        }}>{`{
  "error": "invalid_grant",
  "error_description": "Authorization grant does not match redirect URI",
  "error_uri": "https://auth.firstplatypusbank.com/errors/invalid-grant"
}
`}</code></pre>
      <h3 {...{
        "id": "token-error-codes"
      }}>{`Token error codes`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Error code`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_request`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The request is missing a required parameter.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_client`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Client authentication failed.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_grant`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The provided authorization grant or refresh token is invalid, expired, revoked, does not match the redirection URI used in the authorization request, or was issued to another client (rely on error description from FIs to further break this out).`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`unauthorized_client`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The authenticated client is not authorized to use this authorization grant type.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`unsupported_grant_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The authorization grant type is not supported by the authorization server.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`invalid_scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The requested scope is invalid, unknown, or malformed.`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "expired-access-token"
      }}>{`Expired access token`}</h3>
      <p>{`When Plaid makes API requests with an access token, validate that it hasn't exceeded the lifetime you specified in `}<inlineCode parentName="p">{`expires_in`}</inlineCode>{` during token issuance. Track when each token was issued and check that `}<inlineCode parentName="p">{`current_time - issue_time < expires_in`}</inlineCode>{`.`}</p>
      <p>{`If the access token has expired, send a 401 response with an error code of "602 not authorized". Plaid will automatically use the refresh token to request a new access token.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "flow-at-a-glance",
        level: 2,
        title: "Flow at a glance",
      },
{
        id: "the-oauth-flow-step-by-step",
        level: 2,
        title: "The OAuth flow, step by step",
      },
{
        id: "screen-placement",
        level: 3,
        title: "Screen placement",
      },
{
        id: "step-1---plaid-redirects-the-end-user",
        level: 2,
        title: "Step 1 - Plaid redirects the end user",
      },
{
        id: "query-parameters",
        level: 3,
        title: "Query parameters",
      },
{
        id: "step-2---the-user-completes-all-authentication-steps",
        level: 2,
        title: "Step 2 - The user completes all authentication steps",
      },
{
        id: "two-factor-authentication-2fa",
        level: 3,
        title: "Two-factor authentication (2FA)",
      },
{
        id: "mobile-authentication",
        level: 3,
        title: "Mobile authentication",
      },
{
        id: "step-3---you-create-an-authorization-code-and-send-it-to-plaid",
        level: 2,
        title: "Step 3 - You create an authorization code and send it to Plaid",
      },
{
        id: "example-of-your-response",
        level: 3,
        title: "Example of your response",
      },
{
        id: "step-4---plaid-requests-an-access-token",
        level: 2,
        title: "Step 4 - Plaid requests an access token",
      },
{
        id: "body-parameters",
        level: 3,
        title: "Body parameters",
      },
{
        id: "response-parameters",
        level: 3,
        title: "Response parameters",
      },
{
        id: "error-response",
        level: 3,
        title: "Error response",
      },
{
        id: "step-5---plaid-verifies-the-id-token",
        level: 2,
        title: "Step 5 - Plaid verifies the ID token",
      },
{
        id: "refresh-flow",
        level: 2,
        title: "Refresh flow",
      },
{
        id: "body-parameters-1",
        level: 3,
        title: "Body parameters",
      },
{
        id: "response-parameters-1",
        level: 3,
        title: "Response parameters",
      },
{
        id: "error-response-1",
        level: 3,
        title: "Error response",
      },
{
        id: "quick-troubleshooting",
        level: 2,
        title: "Quick troubleshooting",
      },
{
        id: "when-things-go-wrong",
        level: 2,
        title: "When things go wrong",
      },
{
        id: "user-cancellation",
        level: 3,
        title: "User cancellation",
      },
{
        id: "authorization-error-codes",
        level: 3,
        title: "Authorization error codes",
      },
{
        id: "token-exchange-and-refresh-flow-errors",
        level: 3,
        title: "Token exchange and refresh flow errors",
      },
{
        id: "token-error-codes",
        level: 3,
        title: "Token error codes",
      },
{
        id: "expired-access-token",
        level: 3,
        title: "Expired access token",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
